import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BlockContent from '@sanity/block-content-to-react';
import Select from 'react-select';
import { useFormField, useForm, GenericField } from '../utils/formsV3';
import { AiOutlineExclamationCircle } from 'react-icons/ai';
import { mapBy, wait, withDataLayer } from '../utils/utils';
import clsx from 'clsx';
import { RawPortableText } from '../types';
import serializers, { getGenericLinkSerializer, getReferenceLinkSerializer } from '../serializers';
import InputField from './InputField';
import submittedIcon from '../assets/images/heroForm_submittedIcon.svg';
import CheckboxField from './CheckboxField';

import * as styles from './HeroForm.module.scss';

const customStyles = {
  option: (provided: any, state: any) => ({
    ...provided,
    backgroundColor:
      (state.isSelected && '#F0F0F0') ||
      (state.isFocused && 'rgba(240, 240, 240, 0.5)') ||
      '#ffffff',
    cursor: 'pointer',
    color: '#333',
    padding: 8,
    fontSize: '12px',
    fontFamily: 'Tahoma, sans-serif',
    '&:hover': {
      backgroundColor: state.isSelected ? '#F0F0F0' : 'rgba(240, 240, 240, 0.5)',
    },
  }),
  indicatorSeparator: (provided: any, state: any) => ({
    ...provided,
    display: 'none',
  }),
  placeholder: (provided: any, state: any) => ({
    ...provided,
    fontSize: '14px',
    color: '#828382',
  }),
  control: (provided: any, state: any) => ({
    ...provided,
    backgroundColor: '#fafafa',
    boxShadow: '0 0 0 0 transparent',
    border: state.isFocused ? '1px solid #4b4d4b' : '1px solid #d6d3d3',
    borderRadius: 8,
    opacity: state.isDisabled ? '0.8' : '1',
    '&:hover': {
      borderColor: state.isFocused ? '#4b4d4b' : '#d6d3d3',
    },
  }),
  menu: (provided: any, state: any) => ({
    ...provided,
    padding: 0,
    margin: 0,
    borderRadius: 8,
    overflow: 'hidden',
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
  }),
};

export type FormField = {
  crmFieldId?: string;
  isFieldRequired?: boolean;
} & (
  | {
      fieldType: 'textSingleLine';
      title: string;
      withEmailValidation?: boolean;
    }
  | {
      fieldType: 'textMultiline';
      title: string;
      withEmailValidation?: never;
    }
  | {
      fieldType: 'dropdown';
      title: string;
      withEmailValidation?: never;
      dropdownOptions: Array<{
        title: string;
        crmValueStored: string;
      }>;
    }
  | {
      fieldType: 'checkbox';
      title?: never;
      _rawText: RawPortableText;
      withEmailValidation?: never;
    }
);

type FormFieldWithId = FormField & {
  id: string;
};

interface HeroFormProps {
  title: RawPortableText;
  buttonText: string;
  fieldsConfigs: Array<FormFieldWithId>;
  thankYouScreen: {
    title: string;
    _rawText?: RawPortableText;
  };
  fileUrl?: string;
  className?: string;
}

const HeroForm = ({
  title,
  buttonText,
  fieldsConfigs,
  thankYouScreen,
  fileUrl,
  className,
}: HeroFormProps): React.ReactElement => {
  const { t } = useTranslation();

  const fieldsByName = Object.fromEntries(
    fieldsConfigs.map(field => [
      field.id,
      field.fieldType === 'checkbox'
        ? useFormField<boolean>(false, [...(field.isFieldRequired ? (['required'] as const) : [])])
        : useFormField<string>('', [
            ...(field.isFieldRequired ? (['required'] as const) : []),
            ...(field.withEmailValidation && field.fieldType === 'textSingleLine'
              ? (['email'] as const)
              : []),
          ]),
    ]),
  );

  async function onSubmit() {
    if (process.env.NODE_ENV === 'development') {
      // Code to test form states (should be commented)
      // await wait(2000); // Case loading
      // throw new Error('Got response with status code 400'); // Case unknown error
      // throw new Error('Failed to fetch'); // Case network error
      // return true;  // Case success
    }

    const data = Object.fromEntries(
      fieldsConfigs
        .filter(field => field.crmFieldId)
        .map(({ id, crmFieldId, fieldType }) => [
          crmFieldId,
          { value: fieldsByName[id].value, type: fieldType },
        ]),
    );

    const resp = await fetch('/.netlify/functions/add-contact-to-mailchimp', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (resp.status !== 200) {
      throw new Error('Got response with status code ' + resp.status);
    }
    withDataLayer(dataLayer => {
      dataLayer.push({ event: 'heroForm', type: 'submission' });
    });

    if (fileUrl) {
      window.open(fileUrl);
    }

    return true;
  }

  const { getFieldProps, renderSubmitButton, renderFormMessage, submitState, onFieldUnfocus } =
    useForm({
      fieldsByName,
      onSubmit,
      translateFunction: t,
    });

  const [minFirstScreenContainerHeight, setMinFirstScreenContainerHeight] = useState<number | null>(
    null,
  );
  const firstScreenContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Warning: this code doesn't allow for the container to shrink, only grow
    function updateMinFirstScreenContainerHeight() {
      if (firstScreenContainerRef.current) {
        setMinFirstScreenContainerHeight(
          firstScreenContainerRef.current.getBoundingClientRect().height,
        );
      }
    }
    updateMinFirstScreenContainerHeight();
    window.addEventListener('resize', updateMinFirstScreenContainerHeight);
  }, []);

  return (
    <section className={clsx(styles.section, className)} id="hero-form">
      <div className={styles.container}>
        {(submitState === 'ready' || submitState === 'submitting') && (
          <div className={styles.content} ref={firstScreenContainerRef}>
            <BlockContent
              renderContainerOnSingleChild
              className={styles.title}
              blocks={title}
              serializers={serializers}
            />
            <div className={styles.blockContainer}>
              <div className={styles.infoBlock}>
                {fieldsConfigs.map(formField => {
                  if (formField.fieldType === 'checkbox') {
                    const field = fieldsByName[formField.id] as GenericField<boolean>;
                    return (
                      <div className={clsx(styles.input, styles.checkboxInput)}>
                        <CheckboxField
                          withErrorIcon
                          checkboxLabel={
                            <BlockContent
                              renderContainerOnSingleChild
                              className={styles.text}
                              blocks={formField._rawText}
                              serializers={{
                                ...serializers,
                                marks: {
                                  ...serializers.marks,
                                  genericLink: getGenericLinkSerializer({ openInNewTab: true }),
                                  productLink: getReferenceLinkSerializer({ openInNewTab: true }),
                                  pageLink: getReferenceLinkSerializer({ openInNewTab: true }),
                                  legalDocumentationLink: getReferenceLinkSerializer({
                                    openInNewTab: true,
                                  }),
                                  cropLink: getReferenceLinkSerializer({ openInNewTab: true }),
                                  newsArticleLink: getReferenceLinkSerializer({
                                    openInNewTab: true,
                                  }),
                                  resourceArticleLink: getReferenceLinkSerializer({
                                    openInNewTab: true,
                                  }),
                                },
                              }}
                            />
                          }
                          name={formField.id}
                          {...getFieldProps(field)}
                        />
                      </div>
                    );
                  }

                  const field = fieldsByName[formField.id] as GenericField<string>;
                  let inputElement: React.ReactElement;
                  if (formField.fieldType === 'dropdown') {
                    const dropdownFieldOptions = formField.dropdownOptions.map(dropdownOption => ({
                      value: dropdownOption.crmValueStored,
                      label: dropdownOption.title,
                    }));

                    inputElement = (
                      <>
                        <Select
                          inputId={formField.id + '-input'}
                          name={formField.id}
                          placeholder={t(
                            'hero_form.dropdown_field.placeholder',
                            'Select your {{fieldTitle}}',
                            {
                              fieldTitle: formField.title,
                            },
                          )}
                          styles={customStyles}
                          options={dropdownFieldOptions}
                          className={styles.select}
                          value={
                            dropdownFieldOptions.find(option => option.value === field.value) ||
                            undefined
                          }
                          onChange={currentOption => {
                            field.setValue(currentOption ? currentOption.value : null);
                            field.setError('');
                          }}
                          onBlur={() => onFieldUnfocus(field)}
                          blurInputOnSelect={false}
                        />
                        <div
                          className={!!field.error ? styles.helperText : ''}
                          style={{
                            display: !field.error ? 'none' : undefined,
                          }}
                        >
                          <AiOutlineExclamationCircle />
                          {field.error || 'x'}
                        </div>
                      </>
                    );
                  } else if (formField.fieldType === 'textSingleLine') {
                    inputElement = (
                      <InputField
                        className={clsx(styles.input)}
                        type={formField.withEmailValidation ? 'email' : 'text'}
                        id={formField.id + '-input'}
                        name={formField.id}
                        placeholder={t(
                          'hero_form.single_line_field.placeholder',
                          'Type your {{fieldTitle}}',
                          {
                            fieldTitle: formField.title,
                          },
                        )}
                        helperTextClass={styles.helperText}
                        {...getFieldProps(field)}
                      />
                    );
                  } else if (formField.fieldType === 'textMultiline') {
                    inputElement = (
                      <InputField
                        textarea
                        className={clsx(styles.input, styles.textInput)}
                        id={formField.id + '-input'}
                        name={formField.id}
                        placeholder={t(
                          'hero_form.multiline_field.placeholder',
                          'Type your {{fieldTitle}}',
                          {
                            fieldTitle: formField.title,
                          },
                        )}
                        helperTextClass={styles.helperText}
                        {...getFieldProps(field)}
                      />
                    );
                  } else {
                    //@ts-ignore
                    throw new Error(`Unknown fieldType "${formField.fieldType}"`);
                  }

                  return (
                    <div className={styles.input} key={formField.id}>
                      <label className={styles.label} htmlFor={formField.id + '-input'}>
                        {formField.title}
                      </label>
                      {inputElement}
                    </div>
                  );
                })}

                {renderSubmitButton({
                  labels: {
                    ready: buttonText,
                    submitting: t('hero_form.submitting_button_label', 'Submitting'),
                    submitted: t('hero_form.submitted_button_label', 'Submitted'),
                  },
                  btnClasses: {
                    common: styles.submitButton,
                    ready: styles.formReady,
                    submitting: styles.formSubmitting,
                    submitted: styles.formSubmitted,
                  },
                })}
                {renderFormMessage({
                  styles: {
                    formMessage: styles.formMessage,
                    formMessageSuccess: styles.formMessageSuccess,
                    formMessageError: styles.formMessageError,
                  },
                })}
              </div>
            </div>
          </div>
        )}
        {submitState === 'submitted' && (
          <div
            className={styles.thankYouScreenContainer}
            style={{ minHeight: minFirstScreenContainerHeight || undefined }}
          >
            <div className={styles.iconContainer}>
              <img src={submittedIcon} alt="" className={styles.icon} />
            </div>
            <h2 className={styles.thankYouTitle}>{thankYouScreen.title}</h2>
            {thankYouScreen._rawText && (
              <BlockContent
                renderContainerOnSingleChild
                className={styles.thankYouText}
                blocks={thankYouScreen._rawText}
                serializers={serializers}
              />
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default HeroForm;
